import { z } from "zod/v4";

import { type Tuple, apiTupleSchema, dehydrateTuple, hydrateTuple, rawTupleSchema } from "../Tuple";

export type TableMutation<TOut> =
	| { type: "INSERT"; recordId: string; record: Tuple<TOut> }
	| { type: "UPDATE"; recordId: string; change: RawOperation }
	| { type: "DELETE"; recordId: string };

export const hydrateTableMutation = <T>(raw: RawTableMutation): TableMutation<T> => {
	switch (raw.type) {
		case "INSERT": {
			return { type: "INSERT", recordId: raw.record.id, record: hydrateTuple(raw.record) };
		}
		case "UPDATE": {
			return {
				type: "UPDATE",
				recordId: raw.recordId,
				change: operationSchema.parse(raw.change),
			};
		}
		case "DELETE": {
			return { type: "DELETE", recordId: raw.recordId };
		}
	}
};

export const dehydrateTableMutation = <T>(mutation: TableMutation<T>): RawTableMutation => {
	switch (mutation.type) {
		case "INSERT":
			return { type: "INSERT", record: dehydrateTuple(mutation.record) };
		case "UPDATE":
			return { type: "UPDATE", recordId: mutation.recordId, change: mutation.change };
		case "DELETE":
			return { type: "DELETE", recordId: mutation.recordId };
	}
};

//
// JSON patch operations.
//

const addOperationSchema = z.object({
	op: z.literal("add"),
	path: z.string(),
	value: z.unknown(),
});
const removeOperationSchema = z.object({ op: z.literal("remove"), path: z.string() });
const replaceOperationSchema = z.object({
	op: z.literal("replace"),
	path: z.string(),
	value: z.unknown(),
});

export const operationSchema = z.discriminatedUnion("op", [
	addOperationSchema,
	removeOperationSchema,
	replaceOperationSchema,
]);
export type RawOperation = z.infer<typeof operationSchema>;

//
// Table mutations API, sent to and from the clients to the server.
//

const mutationInsert = z.object({ type: z.literal("INSERT"), record: rawTupleSchema });
const mutationUpdate = z.object({
	type: z.literal("UPDATE"),
	recordId: z.string(),
	change: operationSchema,
});
const mutationDelete = z.object({ type: z.literal("DELETE"), recordId: z.string() });
export const rawTableMutationSchema = z.discriminatedUnion("type", [
	mutationInsert,
	mutationUpdate,
	mutationDelete,
]);
export type RawTableMutation = z.infer<typeof rawTableMutationSchema>;

const apiInsert = z.object({ type: z.literal("INSERT"), record: apiTupleSchema });
export const apiTableMutationSchema = z.discriminatedUnion("type", [
	apiInsert,
	mutationUpdate,
	mutationDelete,
]);
export type ApiTableMutation = z.infer<typeof apiTableMutationSchema>;

export const apiToRawTableMutation = (
	apiTableMutation: ApiTableMutation,
	owner: string | undefined
): RawTableMutation => {
	return apiTableMutation.type === "INSERT"
		? {
				...apiTableMutation,
				record: {
					...apiTableMutation.record,
					owner,
				},
			}
		: apiTableMutation;
};
